import { useMemo, useState } from 'react';
import { FieldPath, SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import { faEdit, faEnvelope, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sendEmail } from 'api/pledges';
import { SelectCampaign } from 'assets/js/pages/pledges/SelectCampaign';
import { useCurrentPledge } from 'assets/js/pages/pledges/useCurrentPledge';
import { InstallmentStatus } from 'assets/js/pages/pledges/usePledgeForm';
import * as Form from 'components/form';
import * as Select from 'components/select';
import { EmptyData } from 'components/util';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccountId } from 'customHooks/useCurrentAccountId';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface SendEmailFormValues {
    campaign?: Select.SelectItemWithLabel<string>[] | null;
    memo?: string;
    email?: string;
}

interface SendEmailPaymentLinkDialog extends AlertDialog.AlertDialogRootProps {}

const SendEmailPaymentLinkDialog = ({ children, ...props }: SendEmailPaymentLinkDialog) => {
    const { data: pledge } = useCurrentPledge();
    const { key, forceRender } = useForceRender();
    const accountId = useCurrentAccountId();
    const queryClient = useQueryClient();
    const [editField, setEditField] = useState<keyof SendEmailFormValues | undefined>();
    const toast = useToast();
    const methods = Form.useGivebutterForm<SendEmailFormValues>({
        defaultValues: {
            campaign: [
                {
                    label: pledge.campaign.title,
                    id: `${pledge.campaign.id}`,
                },
            ],
            memo: pledge.memo,
            email: pledge.contact?.primary_email ?? '',
        },
    });

    const { path, control, handleSubmit, watch, setError } = methods;

    const [memo] = watch(['memo']);

    const { mutate: submit, isPending } = useMutation({
        mutationFn: (form: SendEmailFormValues) => {
            return sendEmail(accountId, pledge.id, {
                ...form,
                campaign_id: form.campaign?.[0]?.id ? parseInt(form.campaign[0].id) : undefined,
            });
        },
        onSuccess: () => {
            toast.success('Email was sent successfully.');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.pledgeById(accountId, pledge.id),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.pledges(accountId),
            });
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            toast.error(error.response?.data?.message ?? 'An unknown error occurred.');
            if (errors) {
                Object.keys(errors).forEach(errorKey => {
                    const key =
                        {
                            campaign_id: 'campaign',
                        }[errorKey] ?? errorKey;

                    const message = errors[errorKey][0];

                    setError(key as FieldPath<SendEmailFormValues>, {
                        message,
                    });
                });
            }
        },
    });

    const activeInstallmentIndex = useMemo(() => {
        return pledge.installments.findIndex(item => item.status === InstallmentStatus.Active);
    }, [pledge.installments]);

    return (
        <AlertDialog.Root key={key} type="info" {...props}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(submit as SubmitHandler<SendEmailFormValues>)}
                        className="contents"
                    >
                        <AlertDialog.Content className="w-full max-w-lg">
                            <AlertDialog.Header>
                                <div className="flex size-12 items-center justify-center rounded-lg border border-gray-200">
                                    <FontAwesomeIcon
                                        className="text-2xl text-purple-600"
                                        icon={faEnvelope}
                                    />
                                </div>
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <AlertDialog.Title>Send pledge payment request</AlertDialog.Title>
                                <AlertDialog.Description className="flex flex-col gap-5">
                                    <p>Review your pledge settings before sharing.</p>
                                    <div className="grid grid-cols-3">
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">
                                                Supporter
                                            </p>
                                            {pledge.contact ? (
                                                <p className="text-gray-600">
                                                    {pledge.contact.first_name}{' '}
                                                    {pledge.contact.last_name}
                                                </p>
                                            ) : (
                                                <EmptyData className="mt-1" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">
                                                Installment
                                            </p>
                                            <p className="text-gray-600">
                                                {activeInstallmentIndex === -1 ? (
                                                    <EmptyData className="mt-1" />
                                                ) : (
                                                    <>
                                                        {activeInstallmentIndex + 1} of{' '}
                                                        {pledge.installments.length}
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">
                                                Installment amount
                                            </p>
                                            <p className="text-gray-600">
                                                {activeInstallmentIndex === -1 ? (
                                                    <EmptyData className="mt-1" />
                                                ) : (
                                                    <>
                                                        <CurrencySymbol />
                                                        {maskDecimal(
                                                            pledge.installments[
                                                                activeInstallmentIndex
                                                            ]?.amount,
                                                        )}
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-sm font-medium text-gray-800">
                                            Campaign credited
                                        </p>
                                        <div className="flex items-center">
                                            {editField === 'campaign' ? (
                                                <Form.Container>
                                                    <SelectCampaign />
                                                    <Form.Error name={path('campaign')} />
                                                </Form.Container>
                                            ) : (
                                                <>
                                                    {pledge.campaign.title ? (
                                                        pledge.campaign.title
                                                    ) : (
                                                        <EmptyData />
                                                    )}
                                                    <Button
                                                        variant="transparent"
                                                        size="sm"
                                                        onClick={() => setEditField('campaign')}
                                                    >
                                                        <FontAwesomeIcon
                                                            className="text-gray-400"
                                                            icon={faEdit}
                                                        />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <Form.Container>
                                        <Form.Label
                                            name={path('email')}
                                            className="text-sm font-medium text-gray-800"
                                        >
                                            Email
                                        </Form.Label>
                                        <Form.TextInputRoot
                                            control={control}
                                            name={'email'}
                                            rules={{ required: 'Email is required' }}
                                        >
                                            <Form.TextInputForm placeholder={'Email'} />
                                        </Form.TextInputRoot>
                                        <Form.UseFormError name={path('email')} />
                                    </Form.Container>
                                    <Form.Container>
                                        <Form.Label
                                            className="text-sm font-medium text-gray-800"
                                            name="memo"
                                        >
                                            Memo
                                        </Form.Label>
                                        <Form.UseFormTextArea
                                            control={control}
                                            name="memo"
                                            maxLength={600}
                                            placeholder="Include a personalized message to your donor"
                                            rows={9}
                                        />
                                        <span className="text-xs text-gray-500">
                                            {memo?.length || 0}/600 characters
                                        </span>
                                        <Form.Error name={path('memo')} />
                                    </Form.Container>
                                </AlertDialog.Description>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <AlertDialog.Action
                                    onClick={e => {
                                        e.preventDefault();
                                        handleSubmit(
                                            submit as SubmitHandler<SendEmailFormValues>,
                                        )();
                                    }}
                                    type="submit"
                                    disabled={isPending}
                                    className="flex gap-4"
                                >
                                    {isPending && (
                                        <FontAwesomeIcon
                                            icon={faSpinnerThird}
                                            className="animate-spin"
                                        />
                                    )}
                                    Send email
                                </AlertDialog.Action>
                            </AlertDialog.Footer>
                        </AlertDialog.Content>
                    </form>
                </Form.Provider>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = SendEmailPaymentLinkDialog;
const SendEmailPaymentLinkTrigger = AlertDialog.Trigger;
const Trigger = SendEmailPaymentLinkTrigger;

export {
    Root,
    Trigger,
    //
    SendEmailPaymentLinkDialog as SendEmailPaymentLink,
    SendEmailPaymentLinkTrigger,
};
